import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Fade,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { aiRows, formatCreated, isComplete, reportFileName } from './botReports';
import { useBotReport } from './useBotReports';

// One report a bot wrote, rendered from its markdown. The markdown is written by an AI, so it is shown as markdown
// only: no raw HTML is rendered, and links open in a new tab.
function MetaRow({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>{children}</Typography>
    </Box>
  );
}

const markdownComponents = {
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
  // A wide table scrolls inside the page instead of stretching it.
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 2 }}>
      <table>{children}</table>
    </Box>
  ),
};

function BotReportPage() {
  const theme = useTheme();
  const { reportId } = useParams();
  const { loading, error, report, reload } = useBotReport(reportId);
  const [copied, setCopied] = useState(false);

  const copyMarkdown = async () => {
    try {
      await navigator.clipboard.writeText(report?.markdown || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (ex) {
      console.error('Could not copy the report:', ex);
    }
  };

  const downloadMarkdown = () => {
    const url = URL.createObjectURL(new Blob([report?.markdown || ''], { type: 'text/markdown;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = reportFileName(report);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const ai = aiRows(report?.ai);

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="lg">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5,
                  '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) } }}>
                  <HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/botreports" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600, px: 1, py: 0.5 }}>
                  Bot Reports
                </Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, px: 1, py: 0.5 }}>
                {report?.title || 'Report'}
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={2}>
          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          {!error && (
            <Grid size={12}>
              <Paper sx={{ p: 2, borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                      {report?.title || (loading ? 'Loading report...' : 'Report')}
                    </Typography>
                    {report && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
                        <Chip size="small" label={report.account} />
                        <Chip size="small" variant="outlined" label={report.botId} />
                        {report.deviceType && <Chip size="small" variant="outlined" label={report.deviceType} />}
                        <Chip
                          size="small"
                          variant="outlined"
                          color={isComplete(report) ? 'success' : 'warning'}
                          label={isComplete(report) ? 'Complete' : 'Incomplete'}
                        />
                      </Stack>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="flex-start" flexShrink={0}>
                    <Button size="small" startIcon={<ContentCopyIcon />} onClick={copyMarkdown} disabled={!report}>
                      {copied ? 'Copied' : 'Copy markdown'}
                    </Button>
                    <Button size="small" startIcon={<DownloadIcon />} onClick={downloadMarkdown} disabled={!report}>
                      Download
                    </Button>
                    <Tooltip title="Refresh">
                      <span>
                        <IconButton onClick={reload} disabled={loading} size="small" aria-label="Refresh">
                          <RefreshIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>

                {report && (
                  <Stack spacing={0.5} sx={{ mt: 2 }}>
                    <MetaRow label="Saved">{formatCreated(report.createdUtc)}</MetaRow>
                    <MetaRow label="Requested by">{report.requestedBy}</MetaRow>
                    <MetaRow label="Bot session">{report.botSessionId}</MetaRow>
                    {ai.map(row => <MetaRow key={row.label} label={row.label}>{row.value}</MetaRow>)}
                  </Stack>
                )}
                {loading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
              </Paper>
            </Grid>
          )}

          {!error && report && !isComplete(report) && (
            <Grid size={12}>
              <Alert severity="warning">The bot saved this report before it finished. It may be missing sections.</Alert>
            </Grid>
          )}

          {!error && report && (
            <Grid size={12}>
              <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
                <Box
                  sx={{
                    wordBreak: 'break-word',
                    '& h1, & h2, & h3, & h4': { mt: 3, mb: 1, fontWeight: 700 },
                    '& h1': { fontSize: '1.7rem' },
                    '& h2': { fontSize: '1.4rem', pb: 0.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}` },
                    '& h3': { fontSize: '1.15rem' },
                    '& p, & li': { lineHeight: 1.65 },
                    '& table': { borderCollapse: 'collapse', width: '100%', fontSize: '0.9rem' },
                    '& th, & td': { border: `1px solid ${alpha(theme.palette.divider, 0.3)}`, padding: '6px 10px', textAlign: 'left', verticalAlign: 'top' },
                    '& th': { background: alpha(theme.palette.primary.main, 0.08) },
                    '& code': {
                      background: alpha(theme.palette.text.primary, 0.08),
                      borderRadius: '4px',
                      px: 0.5,
                      fontSize: '0.88em',
                    },
                    '& pre': { background: alpha(theme.palette.text.primary, 0.06), p: 2, borderRadius: 2, overflowX: 'auto' },
                    '& pre code': { background: 'transparent', p: 0 },
                    '& hr': { border: 'none', borderTop: `2px solid ${alpha(theme.palette.divider, 0.2)}`, my: 3 },
                    '& blockquote': { m: 0, pl: 2, borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.4)}`, color: theme.palette.text.secondary },
                    '& a': { color: theme.palette.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {report.markdown || ''}
                  </ReactMarkdown>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

export default BotReportPage;
